/* eslint-disable max-lines */
import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, signal, inject } from '@angular/core';
import { BehaviorSubject, finalize, map, Subscription, take, tap, Observable, debounceTime, distinctUntilChanged, switchMap, startWith, shareReplay, of, catchError, concat } from 'rxjs';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { OrganizationConfig } from '../../../../models/organization-config.model';
import { ConsumerPortalAdminConfig } from '../../../../models/consumer-portal-admin-config';
import { VersionToggleService } from '../../../../services/version-toggle';
import { SiteSettingService } from '../../../../services/site-setting.service';
import { OrganizationIdService } from '../../../../services/organization-id.service';
import { AppFormValidators } from '../../../validators/app.validators';
import { FileType, SubBannerFileType } from '../../../models/file-type-upload';
import { AdvanceSetupTabs } from '../../../models/advance-tabs';
import isEmpty from 'lodash-es/isEmpty';
import { PorColors } from '../../../../types/por-colors.type';
import { FeatureToggleService } from '../../../../services/feature-toggle.service';
import { AppMediatorService } from '../../../../services/app-mediator.service';
import { CONSUMER_PORTAL_APP_LANG } from '../../../../languages';
import { SelectOptions } from '../../../../shared/components/base-select/base-select.component';
import { DEFAULT_LOCALE } from '../../../../constants/default.const';
import { AppFacadeService } from '../../../../services/app-facade.service';
import { ConfigurationService } from '../../../../services/configuration.service';
import { UploadService } from '../../../services/upload.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoggerService, NotificationService } from '../../../../services';
import { PayMinimumAmountEnum } from '../../../../enums/pay-minimum-amount.enum';
import { OrganizationSmtpService, OrganizationCustomSmtp, SmtpUpsertRequest, SmtpTestConfig } from '../../../../services/organization-smtp.service';

import { Depot } from '../../../../shared/models/depot.interface';
import { Processors } from '../../../../payment/models/processors.interface';
import { OrgDetail } from '../../../../models/organization-detail.model';
import { ExternalCredentials, ExternalCredentialType } from '@por/shared/core';
import { ExternalProviderService } from '../../../../services/external-provider.service';

type DomainCheck = { url: string; orgDetail: OrgDetail | null; isAvailable: boolean | null };

interface DomainCheckState {
    isLoading: boolean;
    isAvailable: boolean | null;
    error: string | null;
}

@Component({
    standalone: false,
    selector: 'por-admin-mandatory',
    templateUrl: './admin-mandatory.component.html',
    styleUrls: ['./admin-mandatory.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdminMandatoryComponent implements OnInit, OnChanges, OnDestroy {
    private readonly fb = inject(FormBuilder);
    private readonly translateService = inject(TranslateService);
    readonly featureToggleService = inject(FeatureToggleService);
    readonly versionService = inject(VersionToggleService);
    private readonly organizationIdService = inject(OrganizationIdService);
    private readonly siteSetting = inject(SiteSettingService);
    private readonly appMediatorService = inject(AppMediatorService);
    readonly appFacadeService = inject(AppFacadeService);
    private readonly configService = inject(ConfigurationService);
    private readonly uploadService = inject(UploadService);
    private readonly notification = inject(NotificationService);
    private readonly logger = inject(LoggerService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly externalProviderService = inject(ExternalProviderService);
    private readonly smtpService = inject(OrganizationSmtpService);

    languageOptions: SelectOptions[];

    constructor() {
        this.languageOptions = inject(CONSUMER_PORTAL_APP_LANG);
    }

    advanceSetupTabs = AdvanceSetupTabs;
    @Input() config!: OrganizationConfig;
    @Input() adminConfig!: ConsumerPortalAdminConfig;
    @Input() showWysiwyg = true;
    @Output() readonly formSubmitted = new EventEmitter<void>();
    @Output() readonly classEmitter = new EventEmitter<string>();
    uploading$ = new BehaviorSubject<boolean>(false);
    selectedPrimaryColor = '';
    selectedSecondaryColor = '';
    domain = '';
    domainName = '';
    payMinimumAmountDueVisible = true;
    subscriptions: Subscription[] = [];
    checkedDomains: DomainCheck[] = [];
    isDialogEnabled = false;

    // Reactive domain checking
    domainCheckState$!: Observable<DomainCheckState>;
    vanityUrlLoading$: Observable<boolean> = of(false);
    vanityUrlAvailable$: Observable<boolean> = of(false);
    payMinimumAmountEnum = PayMinimumAmountEnum;
    fileType = FileType;
    processors = signal<Processors[]>([]);
    depots = signal<Depot[]>([]);
    externalCredentials = signal<ExternalCredentials[]>([]);
    isExternalCredentialsReady = signal<boolean>(false);
    isLoadingTestHapn = signal<boolean>(false);
    logoAllowedExtensions = ['png', 'jpg', 'jpeg'];
    defaultPaymentDepotOptions: SelectOptions[] = [];
    defaultPaymentMidValue?: string;
    hiddenFeatures = signal<string[]>([]);
    isDefaultPaymentMidReady = signal<boolean>(false);

    // SMTP Configuration
    isSmtpConfigReady = signal<boolean>(false);
    isSmtpSaving = signal<boolean>(false);
    isSmtpTesting = signal<boolean>(false);
    hasExistingSmtpConfig = signal<boolean>(false);
    smtpConfigId = signal<number | null>(null);
    // Map encryption types to their standard ports
    /* eslint-disable @typescript-eslint/naming-convention */
    private readonly encryptionPortMap: Record<string, number> = {
        TLS: 587,
        SSL: 465,
        NONE: 25
    };
    /* eslint-enable @typescript-eslint/naming-convention */
    assetTagBaseUrl = signal<string>('');
    mandatoryForm = this.fb.group({
        faviconUrl: ['', [AppFormValidators.imageUrlValidator()]],
        logoUrl: ['', [AppFormValidators.imageUrlValidator(this.logoAllowedExtensions)]],
        primaryColor: ['', [Validators.required, AppFormValidators.hexValidator]],
        secondaryColor: ['', [Validators.required, AppFormValidators.hexValidator]],
        notificationEmail: [''],
        serviceRequestEmail: [''],
        supportEmail: ['', [Validators.required, AppFormValidators.emailValidator]],
        storeName: ['', [Validators.required]],
        vanityUrl: ['', [Validators.required, AppFormValidators.subDomainValidator]],
        externalConsumerPortalURL: ['', [AppFormValidators.urlValidator]],
        banner1: ['', [AppFormValidators.imageUrlValidator()]],
        banner1ExternalUrl: ['', [AppFormValidators.urlValidator]],
        banner2: ['', [AppFormValidators.imageUrlValidator()]],
        banner2ExternalUrl: ['', [AppFormValidators.urlValidator]],
        banner3: ['', [AppFormValidators.imageUrlValidator()]],
        banner3ExternalUrl: ['', [AppFormValidators.urlValidator]],
        banner4: ['', [AppFormValidators.imageUrlValidator()]],
        banner4ExternalUrl: ['', [AppFormValidators.urlValidator]],
        defaultPaymentMid: [''],
        displayServiceCertificate: [true],
        displayInspectionDocument: [true],
        secureUrlLoginRequired: [false],
        locale: [''],
        callOffAbility: [true],
        trackingTab: [true],
        requestServiceAbility: [true],
        multiplePaymentAbility: [false],
        enableAssetTags: [false],
        payMinimumAmountDueText: [{ value: '', disabled: true }, [AppFormValidators.noSpecialCharacters]],
        payMinimumAmountDueType: [PayMinimumAmountEnum.PayMinimumAmount.toString()],
        quotesPaymentAbility: [true],
        liveTracking: [false],
        showZeroQuantityItems: [false],
        enablePrivacyPolicy: [false],
        privacyUrl: ['', [AppFormValidators.urlValidator]],
        hapnClientId: [''],
        hapnClientSecret: [''],
        // SMTP Configuration
        smtpEnabled: [false],
        smtpHost: [''],
        smtpPort: [587, [Validators.min(1), Validators.max(65535)]],
        smtpUser: [''],
        smtpPassword: [''],
        smtpEncryption: ['TLS'],
        smtpFromAddress: ['', [AppFormValidators.emailValidator]],
        autoConvertQuotes: [false]
    });
    advanceViewTab$ = new BehaviorSubject<AdvanceSetupTabs>(AdvanceSetupTabs.accountSetup);

    ngOnInit(): void {
        const lang = this.appMediatorService.localStorageService.selectedContentLanguage;
        if (lang && lang !== this.translateService.getCurrentLang()) {
            this.translateService.use(lang);
        }

        // Initialize reactive domain checking
        this.initializeReactiveDomainChecking();

        if (this.versionService.isAdvanceVersion()) {
            this.notificationEmail?.setValidators([Validators.required, AppFormValidators.emailValidator]);
            this.serviceRequestEmail?.setValidators([AppFormValidators.emailValidator]);
        }

        this.mandatoryForm.get('payMinimumAmountDueType')?.valueChanges.subscribe(value => {
            if (value?.toString() === PayMinimumAmountEnum.Other.toString()) {
                this.mandatoryForm.get('payMinimumAmountDueText')?.clearValidators();
                this.mandatoryForm.get('payMinimumAmountDueText')?.setValidators([Validators.required, AppFormValidators.noSpecialCharacters]);
            } else {
                this.mandatoryForm.get('payMinimumAmountDueText')?.clearValidators();
                this.mandatoryForm.get('payMinimumAmountDueText')?.setValidators([AppFormValidators.noSpecialCharacters]);
            }
            this.mandatoryForm.get('payMinimumAmountDueText')?.updateValueAndValidity();
        });

        this.mandatoryForm.markAllAsTouched();

        // Subscribe to encryption changes to auto-update port
        this.mandatoryForm.get('smtpEncryption')?.valueChanges.subscribe(value => {
            const port = this.encryptionPortMap[value as string] ?? 587;
            this.mandatoryForm.get('smtpPort')?.setValue(port, { emitEvent: false });
        });

        // Subscribe to vanityUrl changes to auto-update assetTagBaseUrl
        this.subscriptions.push(
            this.mandatoryForm.get('vanityUrl')?.valueChanges.subscribe(() => {
                this.updateAssetTagBaseUrl();
            }) as Subscription
        );

        this.appFacadeService.getPaymentProcessors();
        this.appFacadeService.loadOrganizationDepots(this.configService.config?.organizationId ?? '');
        this.appFacadeService.loadExtCredentials(this.configService.config?.organizationId ?? '');

        this.subscriptions.push(
            this.appFacadeService.isPaymentProcessorsAvailable().subscribe(processors => {
                if (Array.isArray(processors)) {
                    this.processors.set(processors);
                    this.createPaymentOptions();
                }
            })
        );
        this.subscriptions.push(
            this.appFacadeService.getOrganizationDepots().subscribe(depots => {
                if (Array.isArray(depots)) {
                    this.depots.set(depots);
                    this.createPaymentOptions();
                }
            })
        );

        if (!this.isFeatureForceHidden('LiveTracking')) {
            this.subscriptions.push(
                this.appFacadeService.getOrganizationExternalCredentials().subscribe(externalCredentials => {
                    if (Array.isArray(externalCredentials)) {
                        this.externalCredentials.set(externalCredentials);
                        this.createExternalCredentials();
                    }
                })
            );
        }

        // SMTP configuration is loaded in ngOnChanges after hidden features are available
    }

    get serviceRequestEmail() {
        return this.mandatoryForm.get('serviceRequestEmail');
    }

    get vanityUrl() {
        return this.mandatoryForm.get('vanityUrl');
    }

    get faviconUrl() {
        return this.mandatoryForm.get('faviconUrl');
    }

    get logoUrl() {
        return this.mandatoryForm.get('logoUrl');
    }

    get primaryColor() {
        return this.mandatoryForm.get('primaryColor');
    }

    get secondaryColor() {
        return this.mandatoryForm.get('secondaryColor');
    }

    get storeName() {
        return this.mandatoryForm.get('storeName');
    }

    get notificationEmail() {
        return this.mandatoryForm.get('notificationEmail');
    }

    get supportEmail() {
        return this.mandatoryForm.get('supportEmail');
    }

    get externalConsumerPortalURL(): FormControl {
        return this.mandatoryForm.get('externalConsumerPortalURL') as FormControl;
    }

    get payMinimumAmountDueText() {
        return this.mandatoryForm.get('payMinimumAmountDueText');
    }

    get amountDueText() {
        switch (this.mandatoryForm.get('payMinimumAmountDueType')?.value) {
            case PayMinimumAmountEnum.PayMinimumAmount.toString():
                return PayMinimumAmountEnum.PayMinimumAmount.toString();
            case PayMinimumAmountEnum.InitialPaymentDue.toString():
                return PayMinimumAmountEnum.InitialPaymentDue.toString();
            default:
                return this.mandatoryForm.get('payMinimumAmountDueText')?.value ?? '';
        }
    }

    get privacyUrl() {
        return this.mandatoryForm.get('privacyUrl');
    }

    get hapnClientId() {
        return this.mandatoryForm.get('hapnClientId');
    }

    get hapnClientSecret() {
        return this.mandatoryForm.get('hapnClientSecret');
    }

    get smtpEnabled() {
        return this.mandatoryForm.get('smtpEnabled');
    }

    get smtpHost() {
        return this.mandatoryForm.get('smtpHost');
    }

    get smtpPort() {
        return this.mandatoryForm.get('smtpPort');
    }

    get smtpUser() {
        return this.mandatoryForm.get('smtpUser');
    }

    get smtpPassword() {
        return this.mandatoryForm.get('smtpPassword');
    }

    get smtpEncryption() {
        return this.mandatoryForm.get('smtpEncryption');
    }

    get smtpFromAddress() {
        return this.mandatoryForm.get('smtpFromAddress');
    }

    get enableAssetTags() {
        return this.mandatoryForm.get('enableAssetTags');
    }

    ngOnChanges(changes: SimpleChanges): void {
        Object.entries(changes).forEach(([key, value]) => {
            if (key === 'config' && value !== undefined) {
                const orgConfig: OrganizationConfig = value.currentValue as OrganizationConfig;
                if (orgConfig?.PrimaryColor) {
                    this.selectedPrimaryColor = orgConfig?.PrimaryColor;
                } else {
                    this.selectedPrimaryColor = this.adminConfig.styling?.themeColors?.primaryColor ?? '';
                }
                this.mandatoryForm.get('primaryColor')?.setValue(this.selectedPrimaryColor);
                if (orgConfig?.SecondaryColor) {
                    this.selectedSecondaryColor = orgConfig?.SecondaryColor;
                } else {
                    this.selectedSecondaryColor = this.adminConfig.styling?.themeColors?.secondaryColor ?? '';
                }
                this.mandatoryForm.get('secondaryColor')?.setValue(this.selectedSecondaryColor);

                if (orgConfig?.LogoUrl) {
                    this.mandatoryForm.get('logoUrl')?.setValue(orgConfig?.LogoUrl);
                }

                if (orgConfig?.NotificationEmail) {
                    this.mandatoryForm.get('notificationEmail')?.setValue(orgConfig?.NotificationEmail);
                }
                if (orgConfig?.StoreName) {
                    this.mandatoryForm.get('storeName')?.setValue(orgConfig?.StoreName);
                }
                if (orgConfig?.FaviconUrl) {
                    this.mandatoryForm.get('faviconUrl')?.setValue(orgConfig?.FaviconUrl);
                }
                if (orgConfig?.SupportEmail) {
                    this.mandatoryForm.get('supportEmail')?.setValue(orgConfig?.SupportEmail);
                }
                if (orgConfig?.VanityURL) {
                    // We have to do this magic because GAPI is storing the domain name without .com
                    // So we have to remove the domain name from the subdomain
                    let domainLabelUrl = this.adminConfig.defaultSubDomain ? this.adminConfig.defaultSubDomain : this.adminConfig.uiUrl ?? '';
                    if (this.adminConfig.production) {
                        domainLabelUrl = 'https://elite.myportal.rent';
                    }
                    this.setDomains(domainLabelUrl);
                    const removedDomainName: string = orgConfig?.VanityURL.replace(this.domainName, '');
                    this.mandatoryForm.get('vanityUrl')?.setValue(removedDomainName);
                }
                if (orgConfig?.ExternalConsumerPortalURL) {
                    this.mandatoryForm.get('externalConsumerPortalURL')?.setValue(orgConfig?.ExternalConsumerPortalURL);
                }

                this.hiddenFeatures.set(orgConfig?.HiddenFeatures ?? []);

                // Load SMTP config now that hidden features are available
                if (!this.isFeatureForceHidden('CustomSmtp')) {
                    this.loadSmtpConfig();
                }

                this.mandatoryForm.get('banner1')?.setValue(orgConfig?.Banner1 ? orgConfig?.Banner1 : '');
                this.mandatoryForm.get('banner1ExternalUrl')?.setValue(orgConfig?.Banner1ExternalUrl ? orgConfig?.Banner1ExternalUrl : '');
                this.mandatoryForm.get('banner2')?.setValue(orgConfig?.Banner2 ? orgConfig?.Banner2 : '');
                this.mandatoryForm.get('banner2ExternalUrl')?.setValue(orgConfig?.Banner2ExternalUrl ? orgConfig?.Banner2ExternalUrl : '');
                this.mandatoryForm.get('banner3')?.setValue(orgConfig?.Banner3 ? orgConfig?.Banner3 : '');
                this.mandatoryForm.get('banner3ExternalUrl')?.setValue(orgConfig?.Banner3ExternalUrl ? orgConfig?.Banner3ExternalUrl : '');
                this.mandatoryForm.get('banner4')?.setValue(orgConfig?.Banner4 ? orgConfig?.Banner4 : '');
                this.mandatoryForm.get('banner4ExternalUrl')?.setValue(orgConfig?.Banner4ExternalUrl ? orgConfig?.Banner4ExternalUrl : '');
                this.mandatoryForm.get('defaultPaymentMid')?.setValue(orgConfig?.DefaultPaymentMid ? orgConfig?.DefaultPaymentMid : '');
                this.mandatoryForm.get('displayServiceCertificate')?.setValue(orgConfig?.DisplayServiceCertificate as boolean);
                this.mandatoryForm.get('displayInspectionDocument')?.setValue(orgConfig?.DisplayInspectionDocument as boolean);
                this.mandatoryForm.get('secureUrlLoginRequired')?.setValue(orgConfig?.SecureUrlLoginRequired as boolean);
                this.mandatoryForm.get('locale')?.setValue(orgConfig?.Locale ?? DEFAULT_LOCALE);
                this.mandatoryForm.get('callOffAbility')?.setValue(orgConfig?.CallOffAbility as boolean);
                this.mandatoryForm.get('trackingTab')?.setValue(orgConfig?.TrackingTab as boolean);
                this.mandatoryForm.get('requestServiceAbility')?.setValue(orgConfig?.RequestServiceAbility as boolean);
                this.mandatoryForm.get('payMinimumAmountDueText')?.setValue(this.getAmountDueTranslatedTextFromConfig(orgConfig?.PayMinimumAmountDueText as string));
                this.mandatoryForm.get('payMinimumAmountDueType')?.setValue(this.getAmountDueTypeValueFromConfig(orgConfig?.PayMinimumAmountDueText as string));
                this.mandatoryForm.get('multiplePaymentAbility')?.setValue(orgConfig?.MultiplePaymentAbility as boolean);
                this.mandatoryForm.get('quotesPaymentAbility')?.setValue(orgConfig?.QuotesPaymentAbility as boolean);
                this.mandatoryForm.get('liveTracking')?.setValue(orgConfig?.LiveTracking as boolean);
                this.mandatoryForm.get('smtpEnabled')?.setValue(!!(orgConfig as unknown as Record<string, boolean>)['CustomSmtp']);
                this.mandatoryForm.get('showZeroQuantityItems')?.setValue(orgConfig?.ShowZeroQuantityItems as boolean);
                this.mandatoryForm.get('serviceRequestEmail')?.setValue(orgConfig?.ServiceRequestEmail ?? '');
                this.mandatoryForm.get('defaultPaymentMid')?.setValue(orgConfig?.DefaultPaymentMid ?? '');
                this.mandatoryForm.get('enablePrivacyPolicy')?.setValue(orgConfig?.EnablePrivacyPolicy as boolean);
                this.mandatoryForm.get('privacyUrl')?.setValue(orgConfig?.PrivacyURL ?? '');
                this.mandatoryForm.get('enableAssetTags')?.setValue(orgConfig?.AssetTags as boolean);
                this.mandatoryForm.get('autoConvertQuotes')?.setValue(orgConfig?.AutoConvertQuotes as boolean);
                this.defaultPaymentMidValue = orgConfig?.DefaultPaymentMid;

                this.updateAssetTagBaseUrl();
                return;
            }
        });
        this.cdr.detectChanges();
    }

    onPayMinimumAmountDueTypeChange(value: string | null): void {
        this.mandatoryForm.get('payMinimumAmountDueType')?.setValue(value);
        if (value === this.payMinimumAmountEnum.Other.toString()) {
            this.mandatoryForm.get('payMinimumAmountDueText')?.setValue('');
        }

        const control = this.mandatoryForm.get('payMinimumAmountDueText');
        if (value === this.payMinimumAmountEnum.Other) {
            control?.enable();
        } else {
            control?.disable();
            control?.reset(); // Optionally reset the value
        }
    }

    setColor(field: string, color: string): void {
        this.mandatoryForm.get(field)?.setValue(color);

        if (field === 'primaryColor') {
            this.selectedPrimaryColor = color;
        }
        if (field === 'secondaryColor') {
            this.selectedSecondaryColor = color;
        }
        const themeColors: PorColors = {
            /* eslint-disable @typescript-eslint/naming-convention */
            /**
             * Directly use in scss property
             */ '--por-color-primary': this.selectedPrimaryColor,
            '--por-color-secondary': this.selectedSecondaryColor,
            '--por-cp-primary-text-color': this.siteSetting.invertColor(this.selectedPrimaryColor),
            '--por-cp-secondary-text-color': this.siteSetting.invertColor(this.selectedSecondaryColor)
        };
        this.siteSetting.setColors(themeColors);
    }

    onSubmit(): void {
        if (!this.mandatoryForm.valid) {
            return;
        }

        const vanityUrl = this.mandatoryForm.get('vanityUrl')?.value ? this.mandatoryForm.get('vanityUrl')?.value + this.domainName : '';
        this.saveOrganizationConfig({
            LogoUrl: this.mandatoryForm.get('logoUrl')?.value ?? '',
            PrimaryColor: this.mandatoryForm.get('primaryColor')?.value ?? this.adminConfig.styling?.themeColors?.primaryColor,
            SecondaryColor: this.mandatoryForm.get('secondaryColor')?.value ?? this.adminConfig.styling?.themeColors?.secondaryColor,
            NotificationEmail: this.mandatoryForm.get('notificationEmail')?.value ?? '',
            ServiceRequestEmail: this.mandatoryForm.get('serviceRequestEmail')?.value ?? '',
            StoreName: this.mandatoryForm.get('storeName')?.value ?? '',
            FaviconUrl: this.mandatoryForm.get('faviconUrl')?.value ?? '',
            SupportEmail: this.mandatoryForm.get('supportEmail')?.value ?? '',
            VanityURL: vanityUrl,
            SubDomain: vanityUrl,
            ExternalConsumerPortalURL: this.mandatoryForm.get('externalConsumerPortalURL')?.value ?? '',
            DisplayServiceCertificate: this.mandatoryForm.get('displayServiceCertificate')?.value ?? true,
            DisplayInspectionDocument: this.mandatoryForm.get('displayInspectionDocument')?.value ?? true,
            SecureUrlLoginRequired: this.mandatoryForm.get('secureUrlLoginRequired')?.value ?? true,
            CallOffAbility: this.mandatoryForm.get('callOffAbility')?.value ?? false,
            Locale: this.mandatoryForm.get('locale')?.value ?? DEFAULT_LOCALE,
            TrackingTab: this.mandatoryForm.get('trackingTab')?.value ?? true,
            RequestServiceAbility: this.mandatoryForm.get('requestServiceAbility')?.value ?? false,
            MultiplePaymentAbility: this.mandatoryForm.get('multiplePaymentAbility')?.value ?? true,
            PayMinimumAmountDueText: this.amountDueText,
            QuotesPaymentAbility: this.mandatoryForm.get('quotesPaymentAbility')?.value ?? false,
            LiveTracking: this.mandatoryForm.get('liveTracking')?.value ?? false,
            ShowZeroQuantityItems: this.mandatoryForm.get('showZeroQuantityItems')?.value ?? false,
            DefaultPaymentMid: this.mandatoryForm.get('defaultPaymentMid')?.value ?? '',
            EnablePrivacyPolicy: this.mandatoryForm.get('enablePrivacyPolicy')?.value ?? false,
            PrivacyURL: this.mandatoryForm.get('privacyUrl')?.value ?? undefined,
            ...(!this.isFeatureForceHidden('LiveTracking') && {
                ExternalCredentials: [
                    {
                        IntegrationType: ExternalCredentialType.Hapn,
                        ClientId: this.mandatoryForm.get('hapnClientId')?.value ?? undefined,
                        Secret: this.mandatoryForm.get('hapnClientSecret')?.value ?? undefined
                    }
                ]
            }),
            ...(!this.isFeatureForceHidden('CustomSmtp') && {
                CustomSmtp: this.mandatoryForm.get('smtpEnabled')?.value ?? false
            }),
            ...(!this.isFeatureForceHidden('AssetTags') && {
                AssetTags: this.mandatoryForm.get('enableAssetTags')?.value ?? false
            }),
            ...(!this.isFeatureForceHidden('AutoConvertQuotes') && {
                AutoConvertQuotes: this.mandatoryForm.get('autoConvertQuotes')?.value ?? false
            })
        } as OrganizationConfig);
        const cssClass = 'submitted-class'; // Replace with the desired class
        this.classEmitter.emit(cssClass);

        // Also save SMTP configuration if not feature hidden
        if (!this.isFeatureForceHidden('CustomSmtp')) {
            this.saveSmtpConfig();
        }
    }

    getAmountDueTranslatedTextFromConfig(orgConfigText: string): string {
        switch (orgConfigText) {
            case PayMinimumAmountEnum.PayMinimumAmount.toString():
                return this.translateService.instant(PayMinimumAmountEnum.PayMinimumAmount.toString());
            case PayMinimumAmountEnum.InitialPaymentDue.toString():
                return this.translateService.instant(PayMinimumAmountEnum.InitialPaymentDue.toString());
            default:
                return orgConfigText ?? '';
        }
    }

    getAmountDueTypeValueFromConfig(orgConfigText: string): string {
        if (!orgConfigText) {
            return PayMinimumAmountEnum.PayMinimumAmount.toString();
        }

        switch (orgConfigText) {
            case PayMinimumAmountEnum.PayMinimumAmount.toString():
                return PayMinimumAmountEnum.PayMinimumAmount.toString();
            case PayMinimumAmountEnum.InitialPaymentDue.toString():
                return PayMinimumAmountEnum.InitialPaymentDue.toString();
            default:
                return PayMinimumAmountEnum.Other.toString();
        }
    }

    saveOrganizationConfig(orgConfigData: OrganizationConfig) {
        this.appFacadeService.setAdminLoaded(true);
        this.configService
            .saveOrganizationConfiguration(orgConfigData)
            .pipe(
                take(1),
                finalize(() => {
                    this.appFacadeService.setAdminLoaded(false);
                })
            )
            .subscribe({
                next: () => {
                    this.formSubmitted.emit();
                    this.isDialogEnabled = true;
                },
                error: () => {
                    this.isDialogEnabled = false;
                }
            });
    }

    checkVanityUrl(url: string): void {
        const cachedDomain: DomainCheck | undefined = this.checkedDomains.find(x => x.url === url);

        if (cachedDomain) {
            if (cachedDomain.isAvailable === false) {
                this.mandatoryForm.get('vanityUrl')?.setErrors({ unavailable: true });
            }
            return;
        }

        // This method is now deprecated - reactive checking is handled in initializeReactiveDomainChecking()
        // Keeping for backward compatibility but functionality is moved to reactive streams

        return;
    }

    updateAssetTagBaseUrl(): void {
        const vanityUrl = this.mandatoryForm.get('vanityUrl')?.value ?? '';
        this.assetTagBaseUrl.set('https://' + [vanityUrl, this.domain].join('.') + '/assetTag/?AssetTagId=');
    }

    private initializeReactiveDomainChecking(): void {
        const vanityUrlControl = this.mandatoryForm.get('vanityUrl');
        if (!vanityUrlControl) return;

        // Create reactive stream for domain validation
        this.domainCheckState$ = vanityUrlControl.valueChanges.pipe(
            startWith(vanityUrlControl.value || ''),
            map((value: string | null) => value || ''), // Convert null to empty string
            debounceTime(1000), // Wait 1 second after user stops typing
            distinctUntilChanged(), // Only emit when value actually changes
            switchMap((url: string) => {
                // Reset validation errors first
                if (vanityUrlControl.hasError('unavailable')) {
                    const errors = { ...vanityUrlControl.errors };
                    delete errors['unavailable'];
                    vanityUrlControl.setErrors(isEmpty(errors) ? null : errors);
                }

                // If URL is empty or form control is invalid, don't check
                if (!url || !url.trim() || vanityUrlControl.invalid) {
                    return of<DomainCheckState>({
                        isLoading: false,
                        isAvailable: null,
                        error: null
                    });
                }

                // Check if this URL was already validated
                const cachedCheck = this.checkedDomains.find(check => check.url === url);
                if (cachedCheck) {
                    if (cachedCheck.isAvailable === false) {
                        vanityUrlControl.setErrors({ ...vanityUrlControl.errors, unavailable: true });
                    }
                    return of<DomainCheckState>({
                        isLoading: false,
                        isAvailable: cachedCheck.isAvailable,
                        error: null
                    });
                }

                // Start loading and make API call
                return concat(
                    of<DomainCheckState>({ isLoading: true, isAvailable: null, error: null }),
                    this.organizationIdService.getOrgIdFromUrl('https://' + url + '.' + this.domain).pipe(
                        map(response => {
                            let isAvailable: boolean | null = null;
                            let orgDetail: OrgDetail | null = null;

                            if (response === false) {
                                // Domain is available
                                isAvailable = true;
                            } else {
                                // Extract organization details from response
                                orgDetail = Array.isArray(response) && response.length > 0 ? response[0] : (response as OrgDetail);

                                if (orgDetail && orgDetail.OrganizationId === this.adminConfig.organizationId) {
                                    // Domain belongs to current organization - show nothing
                                    isAvailable = null;
                                } else {
                                    // Domain belongs to different organization - show error
                                    isAvailable = false;
                                    vanityUrlControl.setErrors({ ...vanityUrlControl.errors, unavailable: true });
                                }
                            }

                            // Cache the result exactly as determined (null means current org; no error)
                            this.checkedDomains.push({ url, orgDetail, isAvailable: isAvailable });

                            return {
                                isLoading: false,
                                isAvailable,
                                error: null
                            } as DomainCheckState;
                        }),
                        catchError(error => {
                            this.logger.logError('Domain check failed: ' + error?.message);
                            return of<DomainCheckState>({
                                isLoading: false,
                                isAvailable: null,
                                error: 'Domain check failed'
                            });
                        })
                    )
                );
            }),
            shareReplay(1) // Cache the latest result
        );

        // Create convenience observables
        this.vanityUrlLoading$ = this.domainCheckState$.pipe(map(state => state.isLoading));

        this.vanityUrlAvailable$ = this.domainCheckState$.pipe(map(state => state.isAvailable === true));
    }

    setDomains(url: string): void {
        const parts: string[] = url.split('.');
        if (parts.length >= 2) {
            const withoutProtocol: string = url.replace('https://', '');
            const withoutFragment: string = withoutProtocol.replace(/\/#$/, '');
            const urlParts: string[] = withoutFragment.split('.');

            // For example https://elite.dev-consumer-portal-ui.rentamation.com/# OR 'https://elite.myportal.rent
            // domain: dev-consumer-portal-ui.rentamation.com / myportal.rent
            // endDomain: .com / .rent
            // domainName: .dev-consumer-portal-ui.rentamation / .myportal
            this.domain = urlParts.slice(1).join('.').replace('/', '');
            const endDomain: string = '.' + urlParts.slice(-1).join('.').replace('/', '');
            this.domainName = '.' + this.domain.replace(endDomain, '');
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.map(sub => sub.unsubscribe());
    }

    afterUploadFile({ url, fileType, subFileType = null }: { url: string | null; fileType: FileType; subFileType: SubBannerFileType | null }): void {
        if (!isEmpty(url)) {
            switch (fileType) {
                case FileType.Banner:
                    this.setBannersValues(url, subFileType);
                    break;
                default:
                    break;
            }
        }
    }
    /**
     * Set/Cache banner values from admin-ad-settings component
     * @param url
     * @param subFileType
     */
    setBannersValues(url: string | null, subFileType: SubBannerFileType | null) {
        switch (subFileType) {
            case SubBannerFileType.Banner1:
                this.mandatoryForm.controls.banner1.setValue(url);
                break;
            case SubBannerFileType.Banner2:
                this.mandatoryForm.controls.banner2.setValue(url);
                break;
            case SubBannerFileType.Banner3:
                this.mandatoryForm.controls.banner3.setValue(url);
                break;
            case SubBannerFileType.Banner4:
                this.mandatoryForm.controls.banner4.setValue(url);
                break;
            default:
                break;
        }
    }

    onModelChange(value: boolean, formControlName: string): void {
        this.mandatoryForm.get(`${formControlName}`)?.setValue(value);
    }

    handleSelectedLocale(value: string): void {
        this.mandatoryForm.controls.locale.setValue(value);
    }

    handleSelectDefaultPaymentDepot(value: string | number): void {
        this.mandatoryForm.controls.defaultPaymentMid.setValue(value.toString());
    }

    handleFile(file: File, fileType: FileType) {
        this.uploading$.next(true);
        this.subscriptions.push(
            this.uploadService
                .uploadFile(file)
                .pipe(
                    take(1),
                    tap({
                        next: response => {
                            if (response.url) {
                                if (!isEmpty(response.url)) {
                                    switch (fileType) {
                                        case FileType.Logo:
                                            this.mandatoryForm.controls.logoUrl.setValue(response.url);
                                            break;
                                        case FileType.Icon:
                                            this.mandatoryForm.controls.faviconUrl.setValue(response.url);
                                            break;
                                        default:
                                            break;
                                    }
                                    // Trigger change detection manually to ensure the form is updated
                                    this.cdr.detectChanges();
                                }
                                this.uploading$.next(false);
                            } else {
                                throw new Error(this.translateService.instant('FileNotUploadedProperly'));
                            }
                        },
                        error: (e: HttpErrorResponse) => {
                            this.notification.error('somethingWentWrong');
                            this.logger.logError(e);
                            this.uploading$.next(false);
                        }
                    })
                )
                .subscribe()
        );
    }

    createPaymentOptions(): void {
        this.defaultPaymentDepotOptions = [];
        if (this.processors().length > 0 && this.depots().length > 0) {
            this.processors().map(processor => {
                const depo = this.depots().find(depot => depot?.Id?.toString() === processor?.RemoteDepotId?.toString());

                if (!depo || Number(depo.Disabled) === 1) {
                    return;
                }

                // don't display depot if not consumer facing
                if (processor.IsCustomerFacing === 0 || !processor.IsCustomerFacing) {
                    return;
                }

                // duplicates
                if (this.defaultPaymentDepotOptions.some(option => option.value === processor?.RemoteDepotId?.toString())) {
                    return;
                }

                this.defaultPaymentDepotOptions.push({
                    label: `${processor?.RemoteDepotId?.toString()} - ${depo?.Name?.toString()}`,
                    value: processor?.RemoteDepotId?.toString() ?? ''
                });
            });

            this.defaultPaymentDepotOptions.sort((a, b) => a.label.localeCompare(b.label));
            // Add empty option only if no default payment depot is set
            if (!this.defaultPaymentMidValue) {
                this.defaultPaymentDepotOptions.unshift({ label: '', value: null });
            } else {
                /**
                 * Check DB stored option is available on option list, if not then set null
                 */
                const isValidOption = this.defaultPaymentDepotOptions.some(option => option.value === String(this.defaultPaymentMidValue));

                this.mandatoryForm.get('defaultPaymentMid')?.setValue(isValidOption ? this.defaultPaymentMidValue : '');

                this.mandatoryForm.get('defaultPaymentMid')?.updateValueAndValidity();
            }

            this.isDefaultPaymentMidReady.set(true);
        }
    }

    createExternalCredentials(): void {
        const hapnCredentials = this.externalCredentials().find(cred => cred.IntegrationType === ExternalCredentialType.Hapn);

        this.mandatoryForm.get('hapnClientId')?.setValue(hapnCredentials?.ClientId ?? '');
        this.mandatoryForm.get('hapnClientSecret')?.setValue(hapnCredentials?.Secret ?? '');

        this.isExternalCredentialsReady.set(true);
    }

    testHapnConnection(): void {
        const clientId = this.mandatoryForm.get('hapnClientId')?.value ?? '';
        const clientSecret = this.mandatoryForm.get('hapnClientSecret')?.value ?? '';

        if (!clientId || !clientSecret) {
            this.notification.error(this.translateService.instant('PleaseProvideValidHAPNCredentials'));
            return;
        }
        this.isLoadingTestHapn.set(true);
        this.externalProviderService
            .testHapnConnection(clientId, clientSecret)
            .pipe(finalize(() => this.isLoadingTestHapn.set(false)))
            .subscribe({
                next: () => this.notification.success(this.translateService.instant('HAPNConnectionSuccessful')),
                error: () => this.notification.error(this.translateService.instant('HAPNConnectionFailed'))
            });
    }

    isFeatureForceHidden(featureName: string): boolean {
        return this.hiddenFeatures().includes(featureName);
    }

    /**
     * Load SMTP configuration for the organization
     */
    loadSmtpConfig(): void {
        const orgId = this.configService.config?.organizationId;
        if (!orgId) {
            this.isSmtpConfigReady.set(true);
            return;
        }

        this.smtpService
            .getSmtpConfig(orgId)
            .pipe(take(1))
            .subscribe({
                next: (config: OrganizationCustomSmtp | null) => {
                    if (config) {
                        this.populateSmtpForm(config);
                    }
                    this.isSmtpConfigReady.set(true);
                    this.cdr.detectChanges();
                },
                error: () => {
                    this.isSmtpConfigReady.set(true);
                    this.cdr.detectChanges();
                }
            });
    }

    /**
     * Populate SMTP form with existing config
     */
    private populateSmtpForm(config: OrganizationCustomSmtp): void {
        this.hasExistingSmtpConfig.set(true);
        this.smtpConfigId.set(config.Id);

        // Don't override smtpEnabled toggle - it's driven by orgConfig.CustomSmtp
        this.mandatoryForm.get('smtpHost')?.setValue(config.Host);
        this.mandatoryForm.get('smtpPort')?.setValue(config.Port);
        this.mandatoryForm.get('smtpUser')?.setValue(config.User);
        this.mandatoryForm.get('smtpEncryption')?.setValue(config.Encryption);
        this.mandatoryForm.get('smtpFromAddress')?.setValue(config.FromAddress);
        // Note: Password is NEVER returned from backend for security
    }

    /**
     * Clear SMTP form fields (after delete)
     */
    private clearSmtpFormFields(): void {
        this.mandatoryForm.get('smtpHost')?.setValue('');
        this.mandatoryForm.get('smtpPort')?.setValue(587);
        this.mandatoryForm.get('smtpUser')?.setValue('');
        this.mandatoryForm.get('smtpPassword')?.setValue('');
        this.mandatoryForm.get('smtpEncryption')?.setValue('TLS');
        this.mandatoryForm.get('smtpFromAddress')?.setValue('');
    }

    /**
     * Save SMTP configuration
     */
    saveSmtpConfig(): void {
        // Guard: Return early if already saving/testing or form invalid
        if (this.isSmtpSaving() || this.isSmtpTesting() || !this.isSmtpFormValid()) {
            return;
        }

        const orgId = this.configService.config?.organizationId;
        if (!orgId) {
            this.notification.error(this.translateService.instant('OrganizationIdMissing'));
            return;
        }

        const smtpEnabled = this.mandatoryForm.get('smtpEnabled')?.value;
        if (!smtpEnabled) {
            // SMTP disabled - delete existing config if any
            if (this.hasExistingSmtpConfig()) {
                this.isSmtpSaving.set(true);
                this.smtpService
                    .deleteSmtpConfig(orgId)
                    .pipe(
                        take(1),
                        finalize(() => {
                            this.isSmtpSaving.set(false);
                            this.cdr.detectChanges();
                        })
                    )
                    .subscribe({
                        next: () => {
                            this.hasExistingSmtpConfig.set(false);
                            this.smtpConfigId.set(null);
                            // Clear SMTP form fields
                            this.clearSmtpFormFields();
                            this.notification.success(this.translateService.instant('SmtpConfigDeleted'));
                        },
                        error: () => {
                            this.notification.error(this.translateService.instant('SmtpConfigDeleteFailed'));
                        }
                    });
            } else {
                this.notification.success(this.translateService.instant('SmtpConfigDisabled'));
            }
            return;
        }

        // Validate required fields when enabled
        const host = this.mandatoryForm.get('smtpHost')?.value;
        const port = this.mandatoryForm.get('smtpPort')?.value;
        const user = this.mandatoryForm.get('smtpUser')?.value;
        const encryption = this.mandatoryForm.get('smtpEncryption')?.value;
        const fromAddress = this.mandatoryForm.get('smtpFromAddress')?.value;
        const password = this.mandatoryForm.get('smtpPassword')?.value;

        if (!host || !port || !user || !encryption || !fromAddress) {
            this.notification.error(this.translateService.instant('SmtpConfigIncomplete'));
            return;
        }

        // Password required for new config
        if (!this.hasExistingSmtpConfig() && !password) {
            this.notification.error(this.translateService.instant('SmtpPasswordRequired'));
            return;
        }

        const request: SmtpUpsertRequest = {
            OrganizationId: parseInt(orgId, 10),
            Host: host,
            Port: port,
            User: user,
            Encryption: encryption as 'TLS' | 'SSL' | 'NONE',
            FromAddress: fromAddress
        };

        // Only include password if provided
        if (password) {
            request.Password = password;
        }

        this.isSmtpSaving.set(true);

        this.smtpService
            .saveSmtpConfig(request)
            .pipe(
                take(1),
                finalize(() => {
                    this.isSmtpSaving.set(false);
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: (result: OrganizationCustomSmtp) => {
                    this.notification.success(this.translateService.instant('SmtpConfigSaved'));
                    this.hasExistingSmtpConfig.set(true);
                    this.smtpConfigId.set(result.Id);
                    // Clear password field after save
                    this.mandatoryForm.get('smtpPassword')?.setValue('');
                },
                error: () => {
                    this.notification.error(this.translateService.instant('SmtpConfigSaveFailed'));
                }
            });
    }

    /**
     * Build SMTP test config from current form values
     * Sends the current form inputs so the backend tests these values, not the saved DB config
     */
    private buildSmtpTestConfig(): SmtpTestConfig {
        return {
            smtpHost: this.mandatoryForm.get('smtpHost')?.value ?? '',
            smtpPort: this.mandatoryForm.get('smtpPort')?.value ?? 587,
            smtpUser: this.mandatoryForm.get('smtpUser')?.value ?? '',
            smtpPassword: this.mandatoryForm.get('smtpPassword')?.value || undefined,
            smtpEncryption: this.mandatoryForm.get('smtpEncryption')?.value ?? 'TLS',
            smtpFromAddress: this.mandatoryForm.get('smtpFromAddress')?.value ?? ''
        };
    }

    /**
     * Send test email using SMTP configuration
     * Test email is sent to the FromAddress configured in SMTP settings
     * Sends current form values so backend tests the actual input, not saved DB config
     */
    testSmtpConnection(): void {
        // Guard: Return early if can't send test email
        if (!this.canSendTestEmail() || this.isSmtpTesting()) {
            return;
        }

        const orgId = this.configService.config?.organizationId;
        const fromAddress = this.mandatoryForm.get('smtpFromAddress')?.value;
        const storeName = this.mandatoryForm.get('storeName')?.value || 'Your Store';

        if (!orgId || !fromAddress) {
            this.notification.error(this.translateService.instant('TestEmailMissingInfo'));
            return;
        }

        if (!this.hasExistingSmtpConfig()) {
            this.notification.error(this.translateService.instant('SmtpConfigNotSaved'));
            return;
        }

        this.isSmtpTesting.set(true);

        const smtpConfig = this.buildSmtpTestConfig();

        this.smtpService
            .sendTestEmail(orgId, fromAddress, storeName, smtpConfig)
            .pipe(
                take(1),
                finalize(() => {
                    this.isSmtpTesting.set(false);
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: response => {
                    if (response.success) {
                        this.notification.success(this.translateService.instant('TestEmailSent', { email: fromAddress }));
                    } else {
                        this.notification.error(this.translateService.instant('TestEmailFailed', { error: response.error }));
                    }
                },
                error: () => {
                    this.notification.error(this.translateService.instant('TestEmailError'));
                }
            });
    }

    /**
     * Check if SMTP form is valid for saving
     */
    isSmtpFormValid(): boolean {
        if (!this.mandatoryForm.get('smtpEnabled')?.value) {
            return true; // Disabled is valid
        }

        const host = this.mandatoryForm.get('smtpHost')?.value;
        const port = this.mandatoryForm.get('smtpPort')?.value;
        const user = this.mandatoryForm.get('smtpUser')?.value;
        const fromAddress = this.mandatoryForm.get('smtpFromAddress')?.value;
        const password = this.mandatoryForm.get('smtpPassword')?.value;

        const hasRequiredFields = !!(host && port && user && fromAddress);
        const hasPassword = this.hasExistingSmtpConfig() || !!password;

        return hasRequiredFields && hasPassword && !this.smtpFromAddress?.invalid;
    }

    /**
     * Check if test email can be sent (config already saved)
     */
    canSendTestEmail(): boolean {
        return this.mandatoryForm.get('smtpEnabled')?.value === true && this.hasExistingSmtpConfig() && this.isSmtpFormValid();
    }

    /**
     * Check if SMTP form is valid for initial save (requires password)
     */
    isSmtpFormValidForSave(): boolean {
        if (!this.mandatoryForm.get('smtpEnabled')?.value) {
            return false;
        }

        const host = this.mandatoryForm.get('smtpHost')?.value;
        const port = this.mandatoryForm.get('smtpPort')?.value;
        const user = this.mandatoryForm.get('smtpUser')?.value;
        const fromAddress = this.mandatoryForm.get('smtpFromAddress')?.value;
        const password = this.mandatoryForm.get('smtpPassword')?.value;

        return !!(host && port && user && fromAddress && password) && !this.smtpFromAddress?.invalid;
    }

    /**
     * Check if test email button should be disabled
     */
    isTestEmailDisabled(): boolean {
        // Disabled during saving or testing
        if (this.isSmtpSaving() || this.isSmtpTesting()) {
            return true;
        }

        // Can send test if already saved and valid
        if (this.canSendTestEmail()) {
            return false;
        }

        // Can save and send if not saved yet but form valid
        if (this.mandatoryForm.get('smtpEnabled')?.value && !this.hasExistingSmtpConfig() && this.isSmtpFormValidForSave()) {
            return false;
        }

        // Otherwise disabled
        return true;
    }

    /**
     * Get the appropriate button label based on state
     * Shows "Save & Send Test Email" when no config exists (needs save first)
     * Shows "Send Test Email" when config already saved
     */
    getTestEmailButtonLabel(): string {
        if (this.isSmtpSaving()) {
            return this.translateService.instant('Saving');
        }
        if (this.isSmtpTesting()) {
            return this.translateService.instant('SendingTestEmail');
        }
        // If no config saved yet, always show "Save & Send" label (disabled if form invalid)
        if (this.mandatoryForm.get('smtpEnabled')?.value && !this.hasExistingSmtpConfig()) {
            return this.translateService.instant('SaveAndSendTestEmail');
        }
        return this.translateService.instant('SendTestEmail');
    }

    /**
     * Handle test email button click
     * Either sends test email (if saved) or saves first then sends
     */
    handleTestEmailClick(): void {
        // Guard: Return early if button should be disabled (por-base-button may not block clicks)
        if (this.isTestEmailDisabled()) {
            return;
        }

        if (!this.hasExistingSmtpConfig()) {
            this.saveAndSendTestEmail();
        } else {
            this.testSmtpConnection();
        }
    }

    /**
     * Save SMTP configuration and then send test email
     */
    saveAndSendTestEmail(): void {
        const orgId = this.configService.config?.organizationId;
        if (!orgId) {
            this.notification.error(this.translateService.instant('OrganizationIdMissing'));
            return;
        }

        const host = this.mandatoryForm.get('smtpHost')?.value ?? '';
        const port = this.mandatoryForm.get('smtpPort')?.value ?? 587;
        const user = this.mandatoryForm.get('smtpUser')?.value ?? '';
        const encryption = this.mandatoryForm.get('smtpEncryption')?.value ?? 'TLS';
        const fromAddress = this.mandatoryForm.get('smtpFromAddress')?.value ?? '';
        const password = this.mandatoryForm.get('smtpPassword')?.value ?? '';

        // Validate required fields (isSmtpFormValidForSave should have checked this)
        if (!host || !port || !user || !fromAddress || !password) {
            this.notification.error(this.translateService.instant('SmtpConfigIncomplete'));
            return;
        }

        const request: SmtpUpsertRequest = {
            OrganizationId: parseInt(orgId, 10),
            Host: host,
            Port: port,
            User: user,
            Password: password,
            Encryption: encryption as 'TLS' | 'SSL' | 'NONE',
            FromAddress: fromAddress
        };

        this.isSmtpSaving.set(true);

        this.smtpService
            .saveSmtpConfig(request)
            .pipe(
                take(1),
                switchMap((result: OrganizationCustomSmtp) => {
                    // Save succeeded - update state
                    this.hasExistingSmtpConfig.set(true);
                    this.smtpConfigId.set(result.Id);
                    // Clear password field after save
                    this.mandatoryForm.get('smtpPassword')?.setValue('');

                    // Now send test email
                    this.isSmtpSaving.set(false);
                    this.isSmtpTesting.set(true);

                    const storeName = this.mandatoryForm.get('storeName')?.value || 'Your Store';
                    return this.smtpService.sendTestEmail(orgId, fromAddress, storeName);
                }),
                finalize(() => {
                    this.isSmtpSaving.set(false);
                    this.isSmtpTesting.set(false);
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                next: response => {
                    if (response.success) {
                        this.notification.success(this.translateService.instant('SmtpConfigSavedAndTestSent', { email: fromAddress }));
                    } else {
                        this.notification.error(this.translateService.instant('SmtpSavedButTestFailed', { error: response.error }));
                    }
                },
                error: () => {
                    // Could be save error or test email error
                    if (!this.hasExistingSmtpConfig()) {
                        this.notification.error(this.translateService.instant('SmtpConfigSaveFailed'));
                    } else {
                        this.notification.error(this.translateService.instant('TestEmailError'));
                    }
                }
            });
    }

    copyAssetTagBaseUrl(): void {
        navigator.clipboard.writeText(this.assetTagBaseUrl());
        this.notification.success(this.translateService.instant('AssetTagBaseUrlCopied'));
    }
}
